/**
 * React Query hooks for account management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountService } from '../services/account';
import type { AccountDetails, UpdateAccountData, DisableAccountData, TransferOwnershipData } from '../types/account';
import toast from 'react-hot-toast';

/**
 * Query key factory for account management
 */
export const accountKeys = {
  all: ['account'] as const,
  detail: () => [...accountKeys.all, 'detail'] as const,
};

/**
 * Get account details
 */
export function useGetAccount() {
  return useQuery({
    queryKey: accountKeys.detail(),
    queryFn: async () => {
      try {
        return await accountService.getAccount();
      } catch (error: any) {
        // For system admins without an account, return null instead of throwing
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry on 404
  });
}

/**
 * Update account settings
 */
export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateAccountData) => accountService.updateAccount(data),
    onMutate: async (newData) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: accountKeys.detail() });

      // Snapshot previous value
      const previousAccount = queryClient.getQueryData<AccountDetails>(accountKeys.detail());

      // Optimistically update account name
      if (previousAccount) {
        queryClient.setQueryData<AccountDetails>(accountKeys.detail(), {
          ...previousAccount,
          name: newData.name,
        });
      }

      return { previousAccount };
    },
    onError: (error: any, _variables, context) => {
      // Rollback on error
      if (context?.previousAccount) {
        queryClient.setQueryData(accountKeys.detail(), context.previousAccount);
      }
      const message = error.response?.data?.message || 'Failed to update account';
      toast.error(message);
    },
    onSuccess: () => {
      toast.success('Account updated successfully');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: accountKeys.detail() });
    },
  });
}

/**
 * Disable account
 */
export function useDisableAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DisableAccountData) => accountService.disableAccount(data),
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to disable account';
      toast.error(message);
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Account disabled successfully');
      // Clear all queries since account is disabled
      queryClient.clear();
      // Redirect will be handled by the component
    },
  });
}

/**
 * Transfer account ownership
 */
export function useTransferOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TransferOwnershipData) => accountService.transferOwnership(data),
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to transfer ownership';
      toast.error(message);
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Ownership transferred successfully');
      // Invalidate account query to refresh owner info
      queryClient.invalidateQueries({ queryKey: accountKeys.detail() });
    },
  });
}
